import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';
import isEmpty from 'lodash/isEmpty';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  newDateOfTopicSelector,
  RESCHEDULE_CLASS_CONFIRMATION_REASON_OPTIONS,
  RESCHEDULE_REASON_MODAL_STAGES,
  rescheduleReasonSelector,
  rescheduleSbatGroupCommunicationsSelector,
  selectedSbatGroupSelector,
  setRescheduleCommunications,
  setRescheduleModalStage,
  setRescheduleReason,
  setRescheduleSbatGroupValidateErrorMessage,
  useValidateUpdateMutation,
} from 'features/classCreation';
import FieldErrors from 'components/FieldErrors';
import GlobalLoader from 'components/GlobalLoader';
import Input from 'components/Input';
import SectionHeading from 'components/SectionHeading';
import Select from 'components/Select';
import SelectNotificationMode from 'components/SelectNotificationMode';
import styles from './RescheduleModalReasonStage.module.scss';
import toDDMMMYYYYHHMM from 'utils/toDDMMMYYYYHHMM';

function RescheduleModalReasonStage() {
  const dispatch = useDispatch();
  const communications = useSelector(rescheduleSbatGroupCommunicationsSelector);
  const rescheduleReason = useSelector(rescheduleReasonSelector);
  const sbatGroup = useSelector(selectedSbatGroupSelector);
  const newDateOfTopic = useSelector(newDateOfTopicSelector);
  const [error, setError] = useState('');
  const [validateUpdate, { isLoading, data, error: validateUpdateError }] =
    useValidateUpdateMutation();

  const rescheduleWithComms = useMemo(
    () => Object.values(communications).some((val) => val),
    [communications]
  );

  const handleReschedule = useCallback(() => {
    if (
      rescheduleReason?.value === 8 &&
      (!rescheduleReason?.customReason ||
        isEmpty(rescheduleReason?.customReason.trim()))
    ) {
      setError('Please enter a reason for rescheduling.');
      return;
    }

    setError('');
    validateUpdate({ sbatGroupIds: [sbatGroup.id] });
  }, [rescheduleReason, validateUpdate, sbatGroup.id]);

  useEffect(() => {
    if (data?.success) {
      dispatch(setRescheduleModalStage(RESCHEDULE_REASON_MODAL_STAGES.confirm));
    } else if (data?.success === false) {
      batch(() => {
        dispatch(
          setRescheduleModalStage(RESCHEDULE_REASON_MODAL_STAGES.validation)
        );
        dispatch(setRescheduleSbatGroupValidateErrorMessage(data?.message));
      });
    }
  });

  const handleReasonChange = useCallback(
    (option) => {
      dispatch(setRescheduleReason(option));
    },
    [dispatch]
  );

  const handleInputReasonChange = useCallback(
    (e) => {
      dispatch(
        setRescheduleReason({
          ...rescheduleReason,
          customReason: e.target.value,
        })
      );
    },
    [dispatch, rescheduleReason]
  );

  const handleCommsToggle = useCallback(
    (commType) => {
      dispatch(
        setRescheduleCommunications({
          channel: commType,
          enabled: !communications[commType],
        })
      );
    },
    [communications, dispatch]
  );

  useEffect(() => {
    if (validateUpdateError) {
      cogoToast.error(DEFAULT_ERROR_MESSAGE);
    }
  }, [validateUpdateError]);

  return (
    <>
      <GlobalLoader isVisible={isLoading} />
      <SectionHeading
        title="Updated Class Details"
        titleClassName={styles.heading}
        className={styles.sectionHeading}
        subtitle="Fill in the following details to send notifications regarding class reschedule."
      />
      <div className="dark bold h5">Update Class Details</div>
      <div className="h6">
        <div className="row">
          <div className="dark m-r-xxs">Previous Date and Time:</div>
          {toDDMMMYYYYHHMM(sbatGroup.date_of_topic)}
        </div>
        <div className="row">
          <div className="dark m-r-xxs">Updated Date and Time:</div>
          {toDDMMMYYYYHHMM(newDateOfTopic)}
        </div>
        <div className="row">
          <div className="dark m-r-xxs">Topic:</div>
          {sbatGroup.academy_topic.title}
        </div>
        <div className="row">
          <div className="dark m-r-xxs">Batch/es:</div>
          {sbatGroup.super_batch_academy_topics
            .map((sbat) => sbat.super_batch.name)
            .join(', ')}
        </div>
      </div>
      <div className={styles.rescheduleReasonField}>
        <Select
          className={styles.reasonSelect}
          placeholder="Select reason for rescheduling class"
          options={RESCHEDULE_CLASS_CONFIRMATION_REASON_OPTIONS}
          value={rescheduleReason}
          onChange={handleReasonChange}
          maxMenuHeight={160}
        />
        {rescheduleReason?.value === 8 && (
          <div>
            <Input
              required
              className={styles.otherReasonInput}
              placeholder="Please Share reason"
              value={rescheduleReason?.customReason || ''}
              onChange={handleInputReasonChange}
            />
            <FieldErrors
              errors={error}
              className={styles.fieldError}
            />
          </div>
        )}
      </div>
      <SelectNotificationMode
        communications={communications}
        handleCommsToggle={handleCommsToggle}
      />
      <Tappable
        className="btn btn-primary btn-long h4"
        onClick={handleReschedule}
      >
        {rescheduleWithComms
          ? 'Reschedule lecture and send notifications'
          : 'Reschedule lecture without sending notifications'}
      </Tappable>
    </>
  );
}

export default RescheduleModalReasonStage;
